import React, { useCallback, useEffect, useRef, type ReactNode } from 'react';
import classnames from 'classnames';
import { useSignal } from '@funtech-int/rival-react';

import styles from './Select.module.css';
import { useSelect } from './useSelect';
import { SelectOptionItem } from './SelectOptionItem';

interface SelectOption {
    value: string;
    label: string;
    icon?: ReactNode;
}

interface SelectProps {
    options: SelectOption[];
    value: string | null;
    onChange: (value: string | null) => void;
    placeholder?: string;
    disabled?: boolean;
    icon?: ReactNode;
    ariaLabel?: string;
    className?: string;
}

export const Select = ({
    className,
    options,
    value,
    onChange,
    placeholder,
    disabled = false,
    icon = null,
    ariaLabel,
}: SelectProps) => {
    const {
        isOpenSignal,
        highlightedIndexSignal,
        selectedOption,
        selectRef,
        listboxId,
        optionId,
        handleKeyDown,
        toggleOpen,
        setHighlightedIndex,
    } = useSelect({ options, value, onChange, disabled });

    const isOpen = useSignal(isOpenSignal);
    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!headerRef.current) return;

        const headerElement = headerRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (isOpenSignal.get() && entry.intersectionRatio < 1) {
                        isOpenSignal.set(false);
                    }
                });
            },
            {
                threshold: [1],
                root: null,
            }
        );

        observer.observe(headerElement);

        return () => {
            observer.disconnect();
        };
    }, [isOpenSignal]);

    const handleMouseEnter = useCallback(
        (index: number) => {
            setHighlightedIndex(index);
        },
        [setHighlightedIndex]
    );

    return (
        <div
            ref={selectRef}
            className={classnames(styles.select, className)}
            role="combobox"
            tabIndex={disabled ? -1 : 0}
            aria-expanded={isOpen}
            aria-disabled={disabled}
            aria-controls={isOpen ? listboxId : undefined}
            aria-haspopup="listbox"
            aria-label={ariaLabel || placeholder}
            onKeyDown={handleKeyDown}
            onClick={toggleOpen}
        >
            <div ref={headerRef} className={styles.header}>
                {icon && (
                    <span className={styles.icon} aria-hidden="true">
                        {icon}
                    </span>
                )}
                <span className={styles.selectedValue}>{selectedOption?.label || placeholder}</span>
                <span className={styles.arrow} aria-hidden="true">
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="m10.175 3.698.424.426-4.176 4.15-.423.42-.423-.42-4.175-4.15.846-.851L6 7.003l3.752-3.73z"
                            fill="currentColor"
                        />
                    </svg>
                </span>
            </div>

            {isOpen && (
                <ul className={styles.optionsList} role="listbox" id={listboxId}>
                    {options.map((option, index) => (
                        <SelectOptionItem
                            key={option.value}
                            option={option}
                            index={index}
                            isSelected={value === option.value}
                            signal={highlightedIndexSignal}
                            optionId={optionId(index)}
                            onMouseEnter={() => handleMouseEnter(index)}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(option.value);
                                isOpenSignal.set(false);
                            }}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};
